import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Hammer, Grid3x3, Ruler, Droplets, Zap, Layers, Bath, ClipboardList,
  Home as HomeIcon, Building2, Factory, Wrench, Palette, CheckCircle2, Phone, MessageCircle, MapPin, Calendar, BookOpen,
} from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { useSEO } from "@/hooks/useSEO";
import { DIVISIONS, IMAGES, FEATURED_PRICE_LISTS, GUIDES, WHY_CHOOSE, INTERIOR_SERVICES } from "@/data/catalog";
import { useContact } from "@/context/ContactContext";
import { api } from "@/lib/api";

const ICONS = { Hammer, Grid3x3, Ruler, Droplets, Zap, Layers, Bath, ClipboardList };
const DIV_ICONS = { "residential-renovation": HomeIcon, "commercial-renovation": Building2, "industrial-renovation": Factory, "metal-works-fabrication": Wrench };

function ServiceBand({ division, image, reversed, accent }) {
  const Icon = DIV_ICONS[division.slug] || Wrench;
  return (
    <div className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${reversed ? "lg:[direction:rtl]" : ""}`}>
      <div className={`${reversed ? "lg:[direction:ltr]" : ""}`}>
        <img src={image} alt={division.title} className="rounded-xl w-full h-64 lg:h-80 object-cover" />
      </div>
      <div className={`${reversed ? "lg:[direction:ltr]" : ""}`}>
        <div className="flex items-center gap-2.5 mb-3">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#0A1128] text-[#D97706]"><Icon size={18} /></span>
          <span className="eyebrow">{division.short} Renovation</span>
        </div>
        <h3 className="text-2xl font-bold text-slate-900">{division.title}</h3>
        <p className="mt-3 text-slate-600 leading-relaxed">{division.intro}</p>
        <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2">
          {division.services.slice(0, 6).map((s) => (
            <li key={s} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 size={15} className="text-[#B87333] shrink-0 mt-0.5" />{s}</li>
          ))}
        </ul>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={`/services/${division.slug}`} data-testid={`home-service-${division.slug}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0A1128] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors">View {division.short} Services <ArrowRight size={15} /></Link>
          <Link to="/request-quotation" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm font-semibold hover:border-[#B87333] hover:text-[#B87333] transition-colors">Request a Quote</Link>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  useSEO({
    title: "HDB Renovation Contractor & Price List Singapore",
    description: "WAAN METAL — HDB-registered, direct renovation contractor in Singapore. Transparent renovation price lists for hacking, tiling, carpentry, plumbing, electrical, kitchen & bathroom, plus office, commercial and metal works.",
    keywords: "renovation contractor Singapore, HDB renovation contractor Singapore, direct renovation contractor Singapore, renovation price Singapore",
  });
  const contact = useContact();
  const [projects, setProjects] = useState([]);
  const waNum = (contact.whatsapp || "").replace(/[^0-9]/g, "");
  const waHref = waNum ? `https://wa.me/${waNum}` : "/request-quotation";

  useEffect(() => { api.get("/projects", { params: { featured: true } }).then(({ data }) => setProjects(data)).catch(() => {}); }, []);

  return (
    <Layout>
      {/* 1. Intro hero — compact, white, service-first */}
      <section className="bg-white border-b border-slate-100" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B87333]/10 text-[#B87333] text-xs font-semibold font-mono-label uppercase tracking-wider">
              <ShieldCheck size={14} /> HDB-Registered Renovation Contractor
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-[#0A1128] tracking-tight leading-[1.08]">
              Renovate, build &amp; fabricate — at <span className="text-[#B87333]">honest, transparent prices.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              From HDB flats to factories: hacking, tiling, carpentry, plumbing, electrical, bathroom packages, commercial fit-out, industrial works and in-house metal fabrication — with clear published rates.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/price-guide" data-testid="hero-price-guide" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#B87333] text-white font-bold hover:bg-[#a9652c] transition-colors">View Price Lists <ArrowRight size={18} /></Link>
              <Link to="/request-quotation" data-testid="hero-request-quotation" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#0A1128] text-white font-bold hover:bg-[#1E293B] transition-colors">Request a Quotation</Link>
              <a href={waHref} target={waNum ? "_blank" : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-200 text-slate-700 font-semibold hover:border-[#25D366] hover:text-[#25D366] transition-colors"><MessageCircle size={18} /> WhatsApp</a>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><CheckCircle2 size={15} className="text-[#B87333]" /> Itemised, no-markup pricing</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={15} className="text-[#B87333]" /> In-house metal workshop</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src={IMAGES.residential1} alt="HDB renovation" className="rounded-xl h-44 lg:h-56 w-full object-cover" />
            <img src={IMAGES.metalFab} alt="Metal fabrication" className="rounded-xl h-44 lg:h-56 w-full object-cover mt-6" />
            <img src={IMAGES.kitchen} alt="Kitchen carpentry" className="rounded-xl h-44 lg:h-56 w-full object-cover" />
            <img src={IMAGES.commercial1} alt="Commercial fit-out" className="rounded-xl h-44 lg:h-56 w-full object-cover mt-6" />
          </div>
        </div>
      </section>

      {/* 3. Featured Renovation Price Lists */}
      <section className="py-14 lg:py-20 bg-slate-50" data-testid="featured-prices-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <p className="eyebrow mb-2">Featured Price Lists</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Renovation rates, out in the open</h2>
            </div>
            <Link to="/price-guide" className="inline-flex items-center gap-1.5 text-[#B87333] font-semibold text-sm hover:gap-2.5 transition-all">See full price guide <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURED_PRICE_LISTS.map((f) => {
              const Icon = ICONS[f.icon] || Hammer;
              return (
                <Link key={f.cat} to={`/price-guide?cat=${encodeURIComponent(f.cat)}`} data-testid={`featured-price-${f.cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group bg-white rounded-xl border border-slate-200 p-5 hover:border-[#B87333] hover:shadow-lg transition-all">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#B87333]/10 text-[#B87333] group-hover:bg-[#B87333] group-hover:text-white transition-colors"><Icon size={20} /></span>
                    <span className="text-xs font-bold text-[#B87333] font-mono-label">{f.teaser}</span>
                  </div>
                  <h3 className="mt-4 font-semibold text-slate-900 text-[15px]">{f.cat}</h3>
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{f.blurb}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4-7. Division service bands */}
      <section className="py-14 lg:py-20 bg-white" data-testid="divisions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 lg:space-y-20">
          <ServiceBand division={DIVISIONS[0]} image={IMAGES.residential2} />
          <ServiceBand division={DIVISIONS[1]} image={IMAGES.commercial2} reversed />
          <ServiceBand division={DIVISIONS[2]} image={IMAGES.industrial1} />
          <ServiceBand division={DIVISIONS[3]} image={IMAGES.gate1} reversed />
        </div>
      </section>

      {/* 8. Interior Design */}
      <section className="py-14 lg:py-20 bg-[#0A1128]" data-testid="interior-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="grid grid-cols-2 gap-3 order-2 lg:order-1">
            <img src={IMAGES.interior1} alt="Interior design" className="rounded-xl h-56 w-full object-cover" />
            <img src={IMAGES.interior2} alt="Interior design" className="rounded-xl h-56 w-full object-cover mt-8" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="eyebrow mb-2">Interior Design Services</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Design &amp; build, under one roof</h2>
            <p className="mt-3 text-slate-300 leading-relaxed">Space planning, concept design and 3D visualisation — coordinated directly with our renovation and fabrication teams so your design is built exactly as drawn.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {INTERIOR_SERVICES.map((s) => <span key={s} className="px-3 py-1.5 rounded-full bg-white/8 border border-white/10 text-slate-200 text-xs">{s}</span>)}
            </div>
            <Link to="/interior-design" data-testid="home-interior-cta" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0A1128] font-bold hover:bg-slate-100 transition-colors"><Palette size={18} /> Explore Interior Design</Link>
          </div>
        </div>
      </section>

      {/* 9. Why choose */}
      <section className="py-14 lg:py-20 bg-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow mb-2">Why Waan Metal</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">A contractor you can hold accountable</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map((w) => (
              <div key={w.title} className="flex gap-4 p-5 rounded-xl border border-slate-100 bg-slate-50">
                <CheckCircle2 size={22} className="text-[#B87333] shrink-0 mt-0.5" />
                <div><h3 className="font-semibold text-slate-900">{w.title}</h3><p className="mt-1 text-sm text-slate-600 leading-relaxed">{w.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Completed projects */}
      <section className="py-14 lg:py-20 bg-slate-50" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div><p className="eyebrow mb-2">Our Work</p><h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Completed projects</h2></div>
            <Link to="/projects" className="inline-flex items-center gap-1.5 text-[#B87333] font-semibold text-sm">All projects <ArrowRight size={16} /></Link>
          </div>
          {projects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {projects.slice(0, 3).map((p) => (
                <div key={p.id} className="rounded-xl overflow-hidden bg-white border border-slate-200" data-testid={`home-project-${p.id}`}>
                  <div className="h-48 bg-slate-200">{p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />}</div>
                  <div className="p-5"><span className="eyebrow">{p.category}</span><h3 className="mt-1 font-semibold text-slate-900">{p.title}</h3>{p.location && <p className="text-sm text-slate-500">{p.location}</p>}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="home-projects-empty">
              <p className="text-slate-600">We publish only verified, completed projects. Our gallery is being updated — ask us for references relevant to your renovation.</p>
              <Link to="/request-quotation" className="mt-4 inline-flex items-center gap-2 text-[#B87333] font-semibold">Request references <ArrowRight size={16} /></Link>
            </div>
          )}
        </div>
      </section>

      {/* 11. Request a quotation band */}
      <section className="bg-[#B87333]" data-testid="home-quote-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Get your free, itemised quotation</h2>
            <p className="mt-2 text-white/90">Share your scope and reference photos — we'll prepare a transparent quote.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/request-quotation" data-testid="home-quote-cta" className="px-7 py-3.5 rounded-full bg-white text-[#0A1128] font-bold hover:bg-slate-100 transition-colors">Request a Quotation</Link>
            <a href={waHref} target={waNum ? "_blank" : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#0A1128] text-white font-bold hover:bg-[#1E293B] transition-colors"><MessageCircle size={18} /> WhatsApp Us</a>
          </div>
        </div>
      </section>

      {/* 12. Renovation guides / articles */}
      <section className="py-14 lg:py-20 bg-white" data-testid="guides-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow mb-2">Renovation Guides</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Plan smarter before you renovate</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {GUIDES.map((g) => (
              <Link key={g.title} to={`/price-guide?cat=${encodeURIComponent(g.cat)}`} data-testid={`guide-${g.cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group rounded-xl border border-slate-200 p-6 hover:border-[#B87333] hover:shadow-lg transition-all">
                <div className="flex items-center gap-2 mb-3"><BookOpen size={16} className="text-[#B87333]" /><span className="eyebrow">{g.tag}</span></div>
                <h3 className="font-semibold text-slate-900 group-hover:text-[#B87333] transition-colors leading-snug">{g.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{g.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[#B87333] text-sm font-semibold">Read the rates <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 13. Contact strip */}
      <section className="py-12 bg-slate-50 border-t border-slate-100" data-testid="home-contact-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-6">
          <div className="flex gap-3"><MapPin size={20} className="text-[#B87333] shrink-0" /><div><p className="font-semibold text-slate-900 text-sm">Visit us</p><p className="text-sm text-slate-600">{contact.address}</p></div></div>
          <div className="flex gap-3"><Phone size={20} className="text-[#B87333] shrink-0" /><div><p className="font-semibold text-slate-900 text-sm">Call / WhatsApp</p><p className="text-sm text-slate-600">{contact.phone || contact.whatsapp || "Request via quotation form"}</p></div></div>
          <div className="flex gap-3"><Calendar size={20} className="text-[#B87333] shrink-0" /><div><p className="font-semibold text-slate-900 text-sm">Hours</p><p className="text-sm text-slate-600">{contact.business_hours}</p></div></div>
        </div>
      </section>
    </Layout>
  );
}
